import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Flame, User, Mail, Phone, Lock, ArrowRight, Gift } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../context/AuthContext';

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [isLogin, setIsLogin] = useState(!refCode);
  const [loading, setLoading] = useState(false);
  const [referrerNick, setReferrerNick] = useState('');

  // Login state
  const [loginVal, setLoginVal] = useState('');
  const [loginPass, setLoginPass] = useState('');

  // Register state
  const [regNick, setRegNick] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPass, setRegPass] = useState('');
  const [regMethod, setRegMethod] = useState('nick');

  // Validate referral code
  useEffect(() => {
    if (refCode) {
      axios.get(`${API}/referral/validate/${refCode}`)
        .then(res => {
          setReferrerNick(res.data.referrer_nick);
          setIsLogin(false);
        })
        .catch(() => {});
    }
  }, [refCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginVal || !loginPass) { toast.error('Wypełnij wszystkie pola'); return; }
    setLoading(true);
    try {
      await login(loginVal, loginPass);
      toast.success('Zalogowano!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd logowania');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regNick || !regPass) { toast.error('Nick i hasło są wymagane'); return; }
    if (regPass.length < 4) { toast.error('Hasło min. 4 znaki'); return; }
    setLoading(true);
    try {
      await register(regNick, regEmail, regPhone, regPass, refCode);
      toast.success(refCode ? 'Konto utworzone! +150 Zorków z polecenia!' : 'Konto utworzone! Witaj w Znajdowniku!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd rejestracji');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Flame className="w-10 h-10 text-primary" strokeWidth={2.5} />
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-white">
              ZNAJDOWNIK
            </h1>
          </div>
          <p className="text-muted-foreground text-sm">Łowca Okazji</p>
        </div>

        {/* Referral Banner */}
        {refCode && referrerNick && (
          <div className="bg-primary/10 border border-primary/30 p-4 mb-4 flex items-center gap-3" data-testid="referral-banner">
            <Gift className="w-8 h-8 text-zorki flex-shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">
                <span className="text-primary font-bold">{referrerNick}</span> zaprasza Cię!
              </p>
              <p className="text-xs text-muted-foreground">
                Załóż konto i otrzymaj <span className="text-zorki font-bold">+150 Zorków</span> na start!
              </p>
            </div>
          </div>
        )}

        <div className="bg-card border border-border/40 p-6">
          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 bg-secondary mb-6">
              <TabsTrigger value="login" data-testid="login-tab" className="font-heading uppercase tracking-wider">
                Logowanie
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab" className="font-heading uppercase tracking-wider">
                Rejestracja
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground mb-1.5 block">Nick, email lub telefon</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="login-input"
                      value={loginVal}
                      onChange={(e) => setLoginVal(e.target.value)}
                      placeholder="Wpisz nick, email lub telefon"
                      className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-1.5 block">Hasło</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="login-password"
                      type="password"
                      value={loginPass}
                      onChange={(e) => setLoginPass(e.target.value)}
                      placeholder="Hasło"
                      className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
                    />
                  </div>
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none h-12"
                >
                  {loading ? 'Logowanie...' : 'Zaloguj się'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Registration method tabs */}
                <div className="flex gap-2 mb-2">
                  {[
                    { id: 'nick', label: 'Nick', icon: User },
                    { id: 'email', label: 'Email', icon: Mail },
                    { id: 'phone', label: 'Telefon', icon: Phone },
                  ].map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      data-testid={`reg-method-${id}`}
                      onClick={() => setRegMethod(id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-heading uppercase tracking-wider border transition-colors ${
                        regMethod === id
                          ? 'border-primary text-primary bg-primary/10'
                          : 'border-border/40 text-muted-foreground hover:border-primary/30'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  ))}
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground mb-1.5 block">Nick *</Label>
                  <Input
                    data-testid="reg-nick"
                    value={regNick}
                    onChange={(e) => setRegNick(e.target.value)}
                    placeholder="Twój unikalny nick"
                    className="bg-input border-transparent focus:border-primary rounded-none"
                  />
                </div>

                {regMethod === 'email' && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-1.5 block">Email</Label>
                    <Input
                      data-testid="reg-email"
                      type="email"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="twoj@email.com"
                      className="bg-input border-transparent focus:border-primary rounded-none"
                    />
                  </div>
                )}

                {regMethod === 'phone' && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-1.5 block">Telefon</Label>
                    <Input
                      data-testid="reg-phone"
                      type="tel"
                      value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value)}
                      placeholder="+48 123 456 789"
                      className="bg-input border-transparent focus:border-primary rounded-none"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-sm text-muted-foreground mb-1.5 block">Hasło *</Label>
                  <Input
                    data-testid="reg-password"
                    type="password"
                    value={regPass}
                    onChange={(e) => setRegPass(e.target.value)}
                    placeholder="Min. 4 znaki"
                    className="bg-input border-transparent focus:border-primary rounded-none"
                  />
                </div>

                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none h-12"
                >
                  {loading ? 'Tworzenie...' : 'Załóż konto'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Bonus powitalny: <span className="text-zorki font-bold">+100 Zorków</span>
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
